import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReaderDocument } from 'web-reader-lib';
import { AnalyticsService } from './services/analytics.service';

@Component({
  selector: 'app-share-fab',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- SHARE FAB + MENU + TOAST -->
    <div
      class="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 pointer-events-none"
    >
      <!-- Toast -->
      <div
        #toast
        class="toast pointer-events-auto px-4 py-2 rounded-full text-sm shadow-lg bg-neutral-900 text-white flex items-center gap-2 transition-all duration-200 transform"
        [class.opacity-0]="!toastVisible"
        [class.translate-y-2]="!toastVisible"
        [class.opacity-100]="toastVisible"
        [class.translate-y-0]="toastVisible"
        role="status"
        aria-live="polite"
      >
        <!-- tiny check icon -->
        <svg
          *ngIf="toastVisible"
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <span>{{ toastMessage }}</span>
      </div>

      <!-- Share menu -->
      <div
        #shareMenu
        class="share-menu pointer-events-auto w-52 rounded-2xl border border-black/10 bg-white shadow-xl overflow-hidden transform origin-bottom-right transition-all duration-150"
        role="menu"
        aria-labelledby="shareButton"
        [class.opacity-0]="!shareMenuOpen"
        [class.scale-95]="!shareMenuOpen"
        [class.translate-y-1]="!shareMenuOpen"
        [class.pointer-events-none]="!shareMenuOpen"
        [class.opacity-100]="shareMenuOpen"
        [class.scale-100]="shareMenuOpen"
        [class.translate-y-0]="shareMenuOpen"
      >
        <div class="py-1">
          <!-- NATIVE SHARE (hidden on Firefox) -->
          <button
            *ngIf="hasNativeShare"
            type="button"
            class="share-menu-item flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-black/5 focus:bg-black/5 outline-none"
            data-action="native"
            role="menuitem"
            (click)="onShareMenuAction('native')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 opacity-70"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M4 12v7a2 2 0 0 0 2 2h4" />
              <path d="M14 21h4a2 2 0 0 0 2-2v-7" />
              <path d="M16 6l-4-4-4 4" />
              <path d="M12 2v14" />
            </svg>
            <span>{{ shareText }}</span>
          </button>

            <!-- COPY LINK (always visible) -->
            <button
              type="button"
              class="share-menu-item flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-black/5 focus:bg-black/5 outline-none"
              data-action="copy"
              role="menuitem"
              (click)="onShareMenuAction('copy')"
            >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 opacity-70"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="8" y="8" width="12" height="12" rx="2" ry="2" />
              <path d="M4 16V6a2 2 0 0 1 2-2h10" />
            </svg>
            <span>{{ copyText }}</span>
          </button>
        </div>
      </div>

      <!-- Share FAB -->
      <button
        #shareButton
        class="share-button pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white shadow-md hover:shadow-lg hover:bg-black/5 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-transparent"
        [title]="shareButtonLabel"
        [aria-label]="shareButtonLabel"
        type="button"
        (click)="onShareClick($event)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="22"
          viewBox="0 -960 960 960"
          width="22"
          fill="currentColor"
          class="text-neutral-700"
          aria-hidden="true"
        >
          <path
            d="M680-80q-50 0-85-35t-35-85q0-6 3-28L282-392q-16 15-37 23.5t-45 8.5q-50 0-85-35t-35-85q0-50 35-85t85-35q24 0 45 8.5t37 23.5l281-164q-2-7-2.5-13.5T560-760q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35q-24 0-45-8.5T598-672L317-508q2 7 2.5 13.5t.5 14.5q0 8-.5 14.5T317-452l281 164q16-15 37-23.5t45-8.5q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T720-200q0-17-11.5-28.5T680-240q-17 0-28.5 11.5T640-200q0 17 11.5 28.5T680-160ZM200-440q17 0 28.5-11.5T240-480q0-17-11.5-28.5T200-520q-17 0-28.5 11.5T160-480q0 17 11.5 28.5T200-440Zm480-280q17 0 28.5-11.5T720-760q0-17-11.5-28.5T680-800q-17 0-28.5 11.5T640-760q0 17 11.5 28.5T680-720Zm0 520ZM200-480Zm480-280Z"
          />
        </svg>
      </button>
    </div>
  `,
})
export class ShareFabComponent implements AfterViewInit, OnDestroy {
  @Input() doc: ReaderDocument | null = null;
  @Input() draftPreview = false;
  @ViewChild('shareButton') shareButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('shareMenu') shareMenuRef?: ElementRef<HTMLDivElement>;
  @ViewChild('toast') toastRef?: ElementRef<HTMLDivElement>;

  shareMenuOpen = false;
  toastVisible = false;
  toastMessage = '';

  readonly isDE = (navigator.language || 'en').toLowerCase().startsWith('de');
  readonly hasNativeShare =
    typeof navigator !== 'undefined' &&
    typeof (navigator as any).share === 'function';

  shareText = this.isDE ? 'Teilen…' : 'Share…';
  copyText = this.isDE ? 'Link kopieren' : 'Copy link';
  sharedText = this.isDE ? 'Geteilt!' : 'Shared!';
  copiedText = this.isDE ? 'Link in Zwischenablage kopiert' : 'Link copied to clipboard';
  failedText = this.isDE ? 'Link konnte nicht kopiert werden' : 'Unable to copy link';

  shareButtonLabel = this.hasNativeShare
    ? (this.isDE ? 'Diese Seite teilen' : 'Share this page')
    : (this.isDE ? 'Link kopieren' : 'Copy link');

  private hideThreshold = 120;
  private hiddenAtBottom = false;
  private lastScrollY = window.scrollY || window.pageYOffset;
  private toastTimeout: any = null;
  private scrollHandler = () => this.onWindowScroll();
  private resizeHandler = () => this.onResizeReposition();

  constructor(
    private analytics: AnalyticsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
  }

  // === PUBLIC HANDLERS ===

  onShareClick(event: MouseEvent) {
    event?.stopPropagation();
    // analytics: pass.shared
    if (this.doc && !this.draftPreview) {
        setTimeout(() => {
        this.analytics.trackPassShared(this.doc!);
        }, 400);
    }
    const willOpen = !this.shareMenuOpen;
    this.setMenu(willOpen);
    if (willOpen) {
        this.positionMenu();
    }
  }

  async onShareMenuAction(action: 'native' | 'copy') {
    this.setMenu(false);

    try {
      if (action === 'native') {
        await this.nativeShare();
      } else if (action === 'copy') {
        await this.copyCurrentUrl();
      }
    } catch (err) {
      console.error('[ShareFab] Error in onShareMenuAction:', err);
      this.showToast(this.failedText);
    }
  }
  // === MENU STATE / POSITION ===

  private setMenu(show: boolean) {
    this.shareMenuOpen = show;
    const menuEl = this.shareMenuRef?.nativeElement;

    if (menuEl) {
      menuEl.classList.toggle('is-hidden', !show);
    }

    if (show) {
      requestAnimationFrame(() => {
        const firstItem = menuEl?.querySelector('.share-menu-item') as HTMLElement | null;
        firstItem?.focus();
      });
    } else {
      this.shareButtonRef?.nativeElement.blur();
    }
  }

  @HostListener('document:click', ['$event'])
  handleGlobalClick(event: MouseEvent) {
    if (!this.shareMenuOpen) return;

    const rawTarget = event.target as Node | null;
    if (!rawTarget) return;

    const el = rawTarget instanceof Element ? rawTarget : rawTarget.parentElement;
    if (!el) return;

    const insideShareButton = !!el.closest('.share-button');
    const insideShareMenu = !!el.closest('.share-menu');

    if (!insideShareButton && !insideShareMenu) {
      this.setMenu(false);
    }
  }

  private positionMenu() {
    const menu = this.shareMenuRef?.nativeElement;
    const btn = this.shareButtonRef?.nativeElement;
    if (!menu || !btn) return;

    const btnRect = btn.getBoundingClientRect();
    menu.style.right = `${window.innerWidth - btnRect.right}px`;
    menu.style.bottom = `${(window.innerHeight - btnRect.bottom) + btnRect.height + 10}px`;
  }

 // === SHARE LOGIC ===

  private currentUrl(): string {
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    return canonical?.href || window.location.href;
  }

  private async nativeShare() {
    if (!this.hasNativeShare) {
      console.warn('[ShareFab] nativeShare called but hasNativeShare=false');
      return;
    }

    const url = this.currentUrl();
    const title = document.title || (this.isDE ? 'Schau dir das an' : 'Check this out');
    const navAny = navigator as any;

    try {
      await navAny.share({ title, url });
      this.showToast(this.sharedText);
    } catch (err) {
      // User cancelled or share failed. NO fallback, NO toast.
      console.warn('[ShareFab] Native share cancelled or failed (no fallback).', err);
    }
  }

  private async copyCurrentUrl() {
    const url = this.currentUrl();

    try {
      await this.copyToClipboard(url);
      //console.log('[ShareFab] copyCurrentUrl -> copied:', url);
      this.showToast(this.copiedText);
    } catch (err) {
      console.error('[ShareFab] copyCurrentUrl -> failed:', err);
      this.showToast(this.failedText);
    }
  }

  private async copyToClipboard(text: string) {
    if (navigator.clipboard && (window as any).isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise<void>((resolve, reject) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();

      try {
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) {
          reject(new Error('document.execCommand("copy") returned false'));
        } else {
          resolve();
        }
      } catch (err) {
        document.body.removeChild(ta);
        reject(err);
      }
    });
  }

  // === TOAST ===

  private showToast(msg: string) {
    this.toastMessage = msg;
    this.toastVisible = true;
    this.cdr.detectChanges();

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastTimeout = setTimeout(() => {
      this.toastVisible = false;
      this.cdr.detectChanges();
    }, 1400);
  }

  // === HIDE NEAR BOTTOM ===

  private onWindowScroll() {
    const shareBtn = this.shareButtonRef?.nativeElement;
    if (!shareBtn) return;

    const y = window.scrollY || window.pageYOffset;
    const dy = y - this.lastScrollY; // < 0 => scrolling up

    const visibleHeight = window.innerHeight;
    const docHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight
    );
    const distanceFromBottom = docHeight - (y + visibleHeight);

    // scroll up → show immediately
    if (dy < 0) {
      shareBtn.classList.remove('hide-bottom');
      this.hiddenAtBottom = false;
      this.lastScrollY = y;
      return;
    }

    // close to bottom → hide
    if (distanceFromBottom < this.hideThreshold && !this.hiddenAtBottom) {
      shareBtn.classList.add('hide-bottom');
      this.hiddenAtBottom = true;
      this.setMenu(false); // hide menu too
    }

    // back up from bottom → show again
    if (distanceFromBottom >= this.hideThreshold && this.hiddenAtBottom) {
      shareBtn.classList.remove('hide-bottom');
      this.hiddenAtBottom = false;
    }

    this.lastScrollY = y;
  }

  private onResizeReposition() {
    if (this.shareMenuOpen) {
      this.positionMenu();
    }
  }
}
